<template>
  <div>
    <el-form
      ref="form"
      size="mini"
      :model="formData"
      :rules="rules"
      :disabled="disabled"
    >
      <el-row :gutter="10">
        <el-col :span="24">
          <el-form-item
            :label="$l('INDEX.form_label_file') || '选择文件'"
            prop="fileCode"
          >
            <el-upload
              ref="upload"
              action="#"
              :auto-upload="false"
              :on-change="handleFileChange"
              :on-remove="handleFileRemove"
              :file-list="fileList"
              :limit="1"
              :on-exceed="handleExceed"
            >
              <el-button size="mini" type="primary">
                {{ $l("INDEX.btn_select_file") || "选择文件" }}
              </el-button>
            </el-upload>
          </el-form-item>
        </el-col>
      </el-row>
      <el-row :gutter="10">
        <el-col :span="12">
          <el-form-item
            :label="$l('INDEX.form_label_docType') || '文档类型'"
            prop="docType"
          >
            <dict-select
              v-model="formData.docType"
              size="mini"
              url="/master/common/dict/biz"
              :placeholder="
                $l('INDEX.placeholder_select_docType') || '请选择文档类型'
              "
              style="width: 100%"
              filterable
              clearable
              :body="{ dictType: 'DocType' }"
            ></dict-select>
          </el-form-item>
        </el-col>
      </el-row>

      <el-row :gutter="10">
        <el-col :span="24">
          <el-form-item
            :label="$l('INDEX.form_label_tech_remark') || '文档说明'"
            prop="remark"
          >
            <el-input
              v-model="formData.remark"
              type="textarea"
              :rows="3"
              :placeholder="
                $l('INDEX.placeholder_tech_input_remark') || '请输入文档说明'
              "
            ></el-input>
          </el-form-item>
        </el-col>
      </el-row>
    </el-form>
  </div>
</template>

<script>
import { mapActions } from "vuex";
import validate from "@/libs/util.validate";
import EditLoading from "@/mixins/edit-loading";

export default {
  mixins: [EditLoading],
  props: {
    disabled: Boolean,
    amzSkuCode: String,
    amzSkuDocCode: String,
    params: { type: Object, default: () => {} },
  },
  data() {
    return {
      formData: {
        amzSkuDocCode: "",
        amzSkuCode: "",
        docGroup: "SKU",
        docType: "",
        fileCode: "",
        fileName: "",
        fileUrl: "",
        remark: "",
      },
      fileList: [],
      pendingFile: null, // 待上传的文件
      rules: {
        docType: [
          {
            required: true,
            message: this.$l("INDEX.tips_tech_docType") || "请选择文档类型",
            trigger: "blur",
          },
        ],
        fileCode: [
          {
            required: true,
            validator: (rule, value, callback) => {
              // 如果有待上传文件或者已有fileCode，则验证通过
              if (this.pendingFile || value) {
                callback();
              } else {
                callback(new Error(this.$l("INDEX.tips_tech_file") || "请选择文件"));
              }
            },
            trigger: "blur",
          },
        ],
      },
    };
  },
  mounted() {
    this.getData();
  },
  methods: {
    ...mapActions("admin/upload", ["push"]),
    getData() {
      if (!this.amzSkuDocCode) {
        // 新增时初始化amzSkuCode
        this.formData.amzSkuCode = this.amzSkuCode;
        return;
      }

      this.$api
        .ecomAmzSkuDocDetailApi({
          amzSkuDocCode: this.amzSkuDocCode,
        })
        .then((res) => {
          this.formData = res.body;
          // 确保amzSkuCode始终使用props传入的值
          this.formData.amzSkuCode = this.amzSkuCode;
          if (res.body.fileName) {
            this.fileList = [
              {
                name: res.body.fileName,
                url: res.body.fileUrl,
              },
            ];
          }
        });
    },
    handleFileChange(file, fileList) {
      if (fileList.length > 0) {
        // 只保存文件引用，不立即上传
        this.pendingFile = file.raw;
        this.formData.fileName = file.name;
      } else {
        this.pendingFile = null;
        this.formData.fileName = "";
      }
    },
    handleExceed() {
      this.$message.warning(
        this.$l("INDEX.tips_tech_file_limit") || "只能上传一个文件"
      );
    },
    handleFileRemove() {
      this.pendingFile = null;
      this.formData.fileName = "";
      // 如果是编辑模式下删除已有文件，也清空 fileCode
      this.formData.fileCode = "";
    },
    async uploadFile(file) {
      return new Promise((resolve) => {
        this.push({
          file,
          added: ({ md5, suffix }) => {
            const fileCode = [md5, suffix].filter(Boolean).join(".");
            this.formData.fileCode = fileCode;
            this.formData.fileName = file.name;
            // 分片上传不返回 fileUrl，可以根据实际情况调整
            this.formData.fileUrl = "";
            // 上传成功后清空待上传文件
            this.pendingFile = null;
            resolve({ fileCode, fileName: file.name });
          },
          fail: () => {
            this.$message.error(
              this.$l("INDEX.tips_tech_upload_fail") || "文件上传失败"
            );
            this.fileList = [];
            this.pendingFile = null;
            resolve(null);
          },
        });
      });
    },
    async save() {
      const result = await validate.formValidate([this.$refs.form]);
      if (!result) {
        return;
      }

      this.loadingOpen();

      // 如果有待上传的文件，先进行分片上传
      if (this.pendingFile) {
        const uploadResult = await this.uploadFile(this.pendingFile);
        if (!uploadResult) {
          this.loadingClose();
          return;
        }
      }

      this.$api
        .ecomAmzSkuDocSaveApi({
          amzSkuCode: this.amzSkuCode,
          ...this.formData,
        })
        .then((res) => {
          this.loadingClose();
          this.$message({
            showClose: true,
            message: this.$l("INDEX.tips_tech_save_ok") || res.msg,
            type: "success",
          });
          this.$emit("close");
          this.$emit("saveReload");
        })
        .catch(() => {
          this.loadingClose();
        });
    },

    cancel() {
      if (this.disabled) {
        this.close();
        return;
      }
      this.openQuestion();
    },
    close() {
      this.$emit("close");
    },
    openQuestion() {
      this.$confirm(
        this.$l("INDEX.tips_tech_question_save") || "是否要保存更改",
        this.$l("INDEX.tips_tech_question_title") || "提示信息",
        {
          distinguishCancelAndClose: true,
          confirmButtonText: this.$l("INDEX.btn_save") || "保存",
          cancelButtonText: this.$l("INDEX.btn_no_save") || "不保存",
        }
      )
        .then(() => {
          this.save();
        })
        .catch((action) => {
          if (action === "cancel") {
            this.close();
          }
        });
    },
  },
};
</script>
